import React, { useEffect, useState } from 'react';
import apiService from '../../services/apiServices';
import {
  Box, Typography, Button, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  Stack, Divider, Skeleton, Alert, Paper,
} from '@mui/material';
import {
  TemplateToolbar, TemplateGrid, TemplateState, TemplateCard, TemplateText, TemplateAction, templateActionSx,
} from '../templates/TemplateShell';
import { Link } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PreviewIcon from '@mui/icons-material/Preview';
import ArticleIcon from '@mui/icons-material/Article';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import Tooltip from '@mui/material/Tooltip';
import { WordPreview } from './WordTemplatePanel';
import ProtocolSettingsDialog from './ProtocolSettingsDialog';
import TagIcon from '@mui/icons-material/Tag';
import { fetchTemplateDocx } from '../../utils/wordTemplate';
import DescriptionIcon from '@mui/icons-material/Description';
import { CONTRACT_TEMPLATE_TAGS } from '../../config/contractTemplateTags';
import Modal from '../actions/Modal';
import Toast from '../actions/Toasts';

// `Image` (foto e punëtorit) e zëvendëson gjenerimi, edhe pse s'është te paleta.
const KNOWN_TAGS = new Set([...CONTRACT_TEMPLATE_TAGS.map((t) => t.key), 'Image']);
// `@` pas një shkronje/pike është email (hr@kompania.com), jo fushë.
const TAG_RE = /(?<![\w.])@(\w+)/g;
const TAG_LABEL = { ...Object.fromEntries(CONTRACT_TEMPLATE_TAGS.map((t) => [t.key, t.label])), Image: 'Foto e punëtorit' };

/** HTML → tekst i lexueshëm: etiketat hiqen DHE entitetet përkthehen (më parë dilte «&nbsp;&nbsp;»). */
function plainText(html) {
  const el = document.createElement('textarea');
  el.innerHTML = String(html || '').replace(/<[^>]*>/g, ' ');
  return el.value.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
}

/**
 * A është shablloni gati për gjenerim?
 *  · fushat — të ndryshme, jo shfaqjet (@FirstName tri herë = një fushë);
 *  · të panjohurat — `@Diçka` që gjenerimi nuk e zëvendëson: del si tekst në kontratë;
 *  · vijat bosh — vende që plotësohen me dorë pas gjenerimit.
 */
export function analyze(html) {
  const text = plainText(html);
  const tags = [...new Set([...text.matchAll(TAG_RE)].map((m) => m[1]))];
  return {
    text,
    words: text ? text.split(' ').filter((w) => /[\p{L}\p{N}]/u.test(w)).length : 0,
    fields: tags.filter((t) => KNOWN_TAGS.has(t)),
    unknown: tags.filter((t) => !KNOWN_TAGS.has(t)),
    blanks: (text.match(/_{3,}|\.{5,}/g) || []).length,
  };
}

const fmtDate = (d) => {
  const x = new Date(d);
  return Number.isNaN(x.getTime()) ? '—' : `${String(x.getDate()).padStart(2, '0')}.${String(x.getMonth() + 1).padStart(2, '0')}.${x.getFullYear()}`;
};

/** Parapamja: fushat theksohen — të njohurat me ngjyrën e markës, të panjohurat me të kuqe. */
export function highlightTags(html) {
  return String(html || '').replace(TAG_RE, (m, k) => (KNOWN_TAGS.has(k)
    ? `<span class="tpl-tag" title="${TAG_LABEL[k]}">${m}</span>`
    : `<span class="tpl-tag tpl-tag-bad" title="Fushë e panjohur — nuk zëvendësohet">${m}</span>`));
}

export default function TemplateList() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [modal, setModal]         = useState({});
  const [rowId, setRowId]         = useState(null);
  const [toast, setToast]         = useState({ open: false, message: '', variant: '' });
  const [preview, setPreview]     = useState(null); // template to preview
  const [protocolOpen, setProtocolOpen] = useState(false);
  const [search, setSearch]       = useState('');

  const load = () => {
    setLoading(true);
    apiService().get('contractsTemplates').then(res => {
      if (res?.success) setTemplates(res.data || []);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const normalizeId = (item) => item?.Id ?? item?.id;

  const handleDelete = (id) => {
    apiService().delete('contractsTemplates', id).then(() => {
      setModal({ open: false });
      setToast({ open: true, message: 'Template u fshi me sukses!', variant: 'success' });
      setTemplates(t => t.filter(x => normalizeId(x) !== id));
    });
  };

  const openModal = (id) => {
    setModal({ open: true, tittle: 'Fshi Template', message: 'A jeni i sigurt që dëshironi ta fshini këtë template?' });
    setToast({ open: false });
    setRowId(id);
  };

  // Ky panel ishte i vetmi pa kërkim — me dhjetëra shabllone kontratash, i vetmi
  // mjet ishte syri. Filtri prek emrin dhe tekstin e pastruar nga HTML-ja.
  const q = search.trim().toLowerCase();
  const filtered = q
    ? templates.filter((t) => {
        const name = t.Name || t.name || '';
        const html = t.Content || t.content || t.Text || t.text || '';
        return name.toLowerCase().includes(q) || plainText(html).toLowerCase().includes(q);
      })
    : templates;

  return (
    <Box>
      <Modal {...modal} handleDelete={() => handleDelete(rowId)} />
      <ProtocolSettingsDialog open={protocolOpen} onClose={() => setProtocolOpen(false)}
        onSaved={(c) => setToast({ open: true, message: `Numërimi u ruajt — kontrata e radhës: ${c.next}`, variant: 'success' })} />
      <Toast {...toast} />

      <TemplateToolbar
        icon={<ArticleIcon />}
        title="Shabllonet e Kontratave"
        hint="Teksti i kontratës me fusha dinamike — @FirstName, @LastName — që mbushen gjatë gjenerimit."
        search={search}
        onSearch={setSearch}
        action={
          <Stack direction="row" spacing={0.75}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<TagIcon sx={{ fontSize: 14 }} />}
            onClick={() => setProtocolOpen(true)}
            sx={templateActionSx}
          >
            Numërimi i protokollit
          </Button>
          <Button
            component={Link}
            to="/contracts-templates/addtemplate"
            variant="contained"
            size="small"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            sx={templateActionSx}
          >
            Shabllon i ri
          </Button>
          </Stack>
        }
      />

      {loading ? (
        <TemplateGrid>
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} variant="rectangular" height={148} sx={{ borderRadius: 2 }} />
          ))}
        </TemplateGrid>
      ) : filtered.length === 0 ? (
        <TemplateState
          empty={q
            ? `Asnjë rezultat për "${search}"`
            : 'Asnjë shabllon kontrate. Shto të parin me "Shabllon i ri".'}
        />
      ) : (
        <TemplateGrid>
          {filtered.map((tpl, idx) => {
            const id   = normalizeId(tpl);
            const name = tpl.Name || tpl.name || 'Shabllon pa emër';
            const html = tpl.Content || tpl.content || tpl.Text || tpl.text || '';
            const isWord = Boolean(tpl.HasDocx);
            // Shablloni Word: fushat janë ato të lidhura te dokumenti, jo @-të e tekstit rezervë.
            const a    = isWord
              ? { ...analyze(html), fields: [...new Set(Object.values(tpl.FieldMap || {}).flatMap((v) => String(v).match(/@(\w+)/g) || []).map((x) => x.slice(1)))], unknown: [], blanks: 0 }
              : analyze(html);
            const preview_text = a.text.slice(0, 160);
            const date = tpl.CreatedAt ? fmtDate(tpl.CreatedAt) : '—';
            const ready = a.fields.length > 0 && !a.unknown.length;

            return (
              <TemplateCard
                key={id || idx}
                title={name}
                titleAdornment={
                  <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                    Krijuar: {date}
                  </Typography>
                }
                actions={
                  <>
                    <TemplateAction label="Parapamje" icon={<PreviewIcon sx={{ fontSize: 14 }} />}
                      onClick={() => setPreview(tpl)} />
                    <TemplateAction label="Ndrysho" icon={<EditIcon sx={{ fontSize: 14 }} />}
                      component={Link} to={`/contracts-templates/edittemplate/${id}`} />
                    {!isWord && (
                      <TemplateAction label="Kopjo si bazë për një shabllon të ri" icon={<ContentCopyIcon sx={{ fontSize: 14 }} />}
                        component={Link} to={{ pathname: '/contracts-templates/addtemplate', state: { copyFrom: { name, html } } }} />
                    )}
                    <TemplateAction label="Fshi" danger icon={<DeleteIcon sx={{ fontSize: 14 }} />}
                      onClick={() => openModal(id)} />
                    <Stack direction="row" spacing={0.5} sx={{ ml: 'auto', flexWrap: 'wrap', justifyContent: 'flex-end' }} useFlexGap>
                      {isWord && (
                        <Tooltip arrow title={`Dokument Word (${tpl.DocxName || '.docx'}) — kontrata del me dizajnin e tij`}>
                          <Chip icon={<DescriptionIcon sx={{ fontSize: '13px !important' }} />} label="Word" size="small"
                            sx={{ height: 20, fontSize: '0.65rem', bgcolor: '#E8F0FE', color: '#1A56DB' }} />
                        </Tooltip>
                      )}
                      <Tooltip arrow title={a.fields.length ? a.fields.map((k) => TAG_LABEL[k]).join(', ') : 'Asnjë fushë dinamike — kontrata del njësoj për të gjithë'}>
                        <Chip label={`${a.fields.length} fusha`} size="small" variant="outlined"
                          color={a.fields.length ? 'primary' : 'default'} sx={{ height: 20, fontSize: '0.65rem' }} />
                      </Tooltip>
                      {a.unknown.length > 0 && (
                        <Tooltip arrow title={`Gjenerimi nuk i njeh: ${a.unknown.map((k) => '@' + k).join(', ')} — do të dalin si tekst në kontratë. Ndreqi te «Ndrysho».`}>
                          <Chip icon={<WarningAmberIcon sx={{ fontSize: '13px !important' }} />} label={`${a.unknown.length} e panjohur`} size="small" color="error" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Tooltip>
                      )}
                      {a.blanks > 0 && (
                        <Tooltip arrow title="Vija bosh (____) që plotësohen me dorë pas gjenerimit">
                          <Chip label={`${a.blanks} për dorë`} size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Tooltip>
                      )}
                      {ready && !a.blanks && (
                        <Tooltip arrow title="Të gjitha fushat njihen nga gjenerimi">
                          <Chip icon={<CheckCircleOutlineIcon sx={{ fontSize: '13px !important' }} />} label="Gati" size="small" color="success" variant="outlined" sx={{ height: 20, fontSize: '0.65rem' }} />
                        </Tooltip>
                      )}
                    </Stack>
                  </>
                }
              >
                <TemplateText lines={3} muted={!preview_text}>
                  {preview_text || 'Pa përmbajtje'}
                </TemplateText>
              </TemplateCard>
            );
          })}
        </TemplateGrid>
      )}

      {/* Preview Dialog */}
      <Dialog
        open={Boolean(preview)}
        onClose={() => setPreview(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ArticleIcon color="primary" />
          {preview?.Name || preview?.name || 'Parapamje'}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {preview?.HasDocx ? <ServerWordPreview id={normalizeId(preview)} name={preview.DocxName} /> : (<>
          {(() => {
            const pa = analyze(preview?.Content || preview?.content || preview?.Text || preview?.text || '');
            return (
              <Alert severity={pa.unknown.length ? 'warning' : 'info'} sx={{ mb: 2, py: 0.5 }}>
                Fushat e theksuara zëvendësohen me të dhënat e punëtorit gjatë gjenerimit ({pa.fields.length} fusha).
                {pa.unknown.length > 0 && <> Me të kuqe: {pa.unknown.map((k) => '@' + k).join(', ')} — gjenerimi s'i njeh dhe dalin si tekst.</>}
                {pa.blanks > 0 && <> {pa.blanks} vija bosh plotësohen me dorë.</>}
              </Alert>
            );
          })()}
          <Paper
            variant="outlined"
            sx={{ p: { xs: 2, sm: 3 }, borderRadius: 1.5, bgcolor: 'grey.50', minHeight: 200 }}
          >
            <Box
              dangerouslySetInnerHTML={{
                __html: highlightTags(preview?.Content || preview?.content || preview?.Text || preview?.text) ||
                  '<p style="color:#999;font-style:italic">Pa përmbajtje</p>',
              }}
              sx={{
                fontFamily: 'Georgia, serif',
                fontSize: '0.9rem',
                lineHeight: 1.8,
                '& h1, & h2, & h3': { color: 'primary.main', mt: 2, mb: 1 },
                '& h2': { fontSize: '1.2rem', fontWeight: 700 },
                '& h3': { fontSize: '1rem',   fontWeight: 600 },
                '& p':  { mb: 1.2 },
                '& hr': { borderColor: 'divider', my: 2 },
                '& strong': { fontWeight: 700 },
                '& .tpl-tag': { bgcolor: 'rgba(47,125,144,0.12)', color: '#1D5462', borderRadius: '4px', px: 0.5, fontFamily: 'inherit', fontWeight: 600 },
                '& .tpl-tag-bad': { bgcolor: 'rgba(220,38,38,0.12)', color: '#B91C1C' },
                '& table': { width: '100%', borderCollapse: 'collapse' },
              }}
            />
          </Paper>
          </>)}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setPreview(null)} color="inherit">Mbyll</Button>
          {preview && (
            <Button
              component={Link}
              to={`/contracts-templates/edittemplate/${normalizeId(preview)}`}
              variant="contained"
              startIcon={<EditIcon />}
              onClick={() => setPreview(null)}
            >
              Ndrysho
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
}

/** Parapamja e shabllonit Word — dokumenti i ruajtur, ashtu siç duket në Word. */
function ServerWordPreview({ id, name }) {
  const [file, setFile] = React.useState(null);
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => {
    let alive = true;
    fetchTemplateDocx(id, name).then((f) => { if (!alive) return; if (f) setFile(f); else setFailed(true); });
    return () => { alive = false; };
  }, [id, name]);
  if (failed) return <Alert severity="warning">Dokumenti Word nuk u ngarkua.</Alert>;
  if (!file) return <Box sx={{ py: 6, textAlign: 'center', color: 'text.secondary' }}>Duke ngarkuar dokumentin…</Box>;
  return <WordPreview file={file} />;
}
